<script setup lang="ts">
import { computed, useSlots, type PropType } from 'vue';
import { alertProps } from 'naive-ui';
import GaiaStatusIndicator from '../base/GaiaStatusIndicator.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaFonts } from '../themes/tokens';

const props = defineProps({
  ...alertProps,
  // Gaia banners default to the tinted danger strip from the CM mocks.
  type: {
    type: String as PropType<'default' | 'info' | 'success' | 'warning' | 'error'>,
    default: 'error',
  },
  /**
   * Drop the leading dot. Inside an auth card the strip is the only thing on
   * the surface and its tint already carries the tone, so the dot reads as
   * decoration rather than as the signal it is in a dense table.
   */
  plain: {
    type: Boolean,
    default: false,
  },
});

// Chrome comes from the Alert theme section; the icon is our status dot.
const passThrough = computed(() => {
  const { showIcon, plain, ...rest } = props;
  return rest;
});

const monoFont = gaiaFonts.mono;

const slots = useSlots();
/** Block form with something in the title row's leading slot — the lines below indent under it. */
const hasTitleDot = computed<boolean>(
  () => Boolean(props.title) && (slots.icon !== undefined || !props.plain)
);

const dotStatus = computed(
  () =>
    (
      ({
        error: 'alert',
        warning: 'warning',
        success: 'success',
        // Info banners are the violet "previewing version" strip (File Detail).
        info: 'accent',
        default: 'neutral',
      }) as const
    )[props.type]
);
</script>

<template>
  <!-- Tinted inline banner (stale data, save errors) from the CM mocks —
       a themed n-alert with the Gaia status dot and an inline #action slot. -->
  <n-alert v-bind="passThrough" :show-icon="false" :title="undefined" class="gaia-banner">
    <!-- Inline dot + message + right-aligned action (or, with `title`, the
         block form: title, body, mono meta, action row — the load-error
         card). n-alert has no layout slots for this; colors come from the
         Alert theme section. -->
    <n-flex
      :align="props.title ? 'flex-start' : 'center'"
      justify="space-between"
      :size="10"
      :wrap="false"
      :style="props.title ? { padding: '6px 6px 4px' } : undefined"
    >
      <!-- Inline form: the dot sits on the one line with the message. -->
      <template v-if="!props.title">
        <span v-if="$slots.icon" class="dot icon"><slot name="icon" /></span>
        <gaia-status-indicator v-else-if="!props.plain" :status="dotStatus" class="dot" />
      </template>
      <n-flex vertical :size="4" :style="{ minWidth: 0, flex: 1 }">
        <!-- Block form: the dot rides the title's own row, so flex centring
             puts it on the title's midline whatever the line height — no
             hand-tuned nudge. The lines under the title are indented by the
             dot's width plus the gap so they start under the title text. -->
        <n-flex v-if="props.title" align="center" :size="10" :wrap="false" :style="{ minWidth: 0 }">
          <span v-if="$slots.icon" class="dot icon"><slot name="icon" /></span>
          <gaia-status-indicator v-else-if="!props.plain" :status="dotStatus" class="dot" />
          <gaia-text size="subtitle" weight="semibold" tone="ink">{{ props.title }}</gaia-text>
        </n-flex>
        <span class="msg" :class="{ indented: hasTitleDot }"><slot /></span>
        <gaia-text
          v-if="$slots.meta"
          size="micro"
          tone="muted"
          :class="{ indented: hasTitleDot }"
          :style="{ fontFamily: monoFont, marginTop: '2px' }"
        >
          <slot name="meta" />
        </gaia-text>
        <n-flex
          v-if="$slots.actions"
          :size="8"
          :wrap="false"
          :class="{ indented: hasTitleDot }"
          :style="{ marginTop: '12px' }"
        >
          <slot name="actions" />
        </n-flex>
      </n-flex>
      <span v-if="$slots.action" class="action"><slot name="action" /></span>
    </n-flex>
  </n-alert>
</template>

<style scoped>
/* Inline holders for a slotted icon and the trailing action: both must sit
   inline against the message rather than becoming block flex rows. */
.icon,
.action {
  flex: none;
  display: inline-flex;
  align-items: center;
}

/* Under a titled banner's dot: the 13px glyph plus the row's 10px gap. */
.indented {
  padding-left: 23px;
}
</style>
